import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import type { ProjectMember } from '@/types'
import { useToast } from '../ToastContainer'
import {
  fetchProjectFieldAssignments,
  updateProjectFieldAssignments,
  type FieldAssignmentSummary,
} from '../../services/api/projectFormAssignments'

interface FieldAssignmentModalProps {
  open: boolean
  projectId: string
  templateId: string
  templateName: string
  members: ProjectMember[]
  onClose: () => void
}

const frequencyLabelMap: Record<string, string> = {
  DAILY: '每日',
  WEEKLY: '每週',
  MONTHLY: '每月',
  QUARTERLY: '每季',
}

export const FieldAssignmentModal = ({
  open,
  projectId,
  templateId,
  templateName,
  members,
  onClose,
}: FieldAssignmentModalProps) => {
  const { showError, showSuccess } = useToast()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fields, setFields] = useState<FieldAssignmentSummary[]>([])
  const [error, setError] = useState<string | null>(null)

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => a.user.name.localeCompare(b.user.name, 'zh-TW'))
  }, [members])

  useEffect(() => {
    if (!open) {
      return
    }

    const loadAssignments = async () => {
      try {
        setLoading(true)
        const response = await fetchProjectFieldAssignments(projectId, templateId)
        const fetchedFields = response.data?.fields ?? []
        setFields(fetchedFields)
        setError(null)
      } catch (err: any) {
        const message = err?.response?.data?.message ?? err?.message ?? '讀取欄位負責人失敗'
        setError(message)
        showError('讀取欄位負責人失敗', message)
      } finally {
        setLoading(false)
      }
    }

    loadAssignments()
  }, [open, projectId, templateId, showError])

  const updateFieldAssignee = (fieldKey: string, assigneeUserId: string | null) => {
    setFields(prev =>
      prev.map(field =>
        field.fieldKey === fieldKey ? { ...field, assigneeUserId } : field,
      ),
    )
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const payload = fields.map(field => ({
        fieldKey: field.fieldKey,
        assigneeUserId: field.assigneeUserId ?? null,
      }))
      const response = await updateProjectFieldAssignments(projectId, templateId, payload)
      setFields(response.data?.fields ?? [])
      showSuccess('欄位負責人已更新')
      setError(null)
      onClose()
    } catch (err: any) {
      const message = err?.response?.data?.message ?? err?.message ?? '更新欄位負責人失敗'
      setError(message)
      showError('更新欄位負責人失敗', message)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  {templateName} · 欄位負責人
                </Dialog.Title>
                <p className="mt-1 text-sm text-gray-500">
                  針對標記檢查頻率的欄位設定專案負責人。若未指定，系統仍會建立提醒 Issue，但保持未指派狀態。
                </p>

                <div className="mt-4 space-y-4">
                  {loading ? (
                    <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                      讀取欄位資訊中…
                    </div>
                  ) : fields.length === 0 ? (
                    <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                      尚未在此模板設定檢查頻率的欄位。
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto rounded-md border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">欄位名稱</th>
                            <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">頻率</th>
                            <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">負責人</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {fields.map(field => (
                            <tr key={field.fieldKey}>
                              <td className="px-4 py-3 align-top">
                                <div className="font-medium text-gray-900">{field.label}</div>
                                {field.sectionTitle && (
                                  <div className="text-xs text-gray-500">區塊：{field.sectionTitle}</div>
                                )}
                                {field.description && (
                                  <p className="mt-1 text-xs text-gray-500">{field.description}</p>
                                )}
                              </td>
                              <td className="px-4 py-3 align-top text-gray-700">
                                {frequencyLabelMap[field.frequency] ?? field.frequency}
                              </td>
                              <td className="px-4 py-3 align-top">
                                <select
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                                  value={field.assigneeUserId ?? ''}
                                  onChange={(event) => {
                                    const value = event.target.value
                                    updateFieldAssignee(field.fieldKey, value ? value : null)
                                  }}
                                  disabled={saving}
                                >
                                  <option value="">未指派</option>
                                  {sortedMembers.map(member => (
                                    <option key={member.userId} value={member.userId}>
                                      {member.user.name}（{member.user.email}）
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-70"
                    disabled={saving}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={saving || loading || fields.length === 0}
                  >
                    {saving ? '儲存中…' : '儲存設定'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default FieldAssignmentModal
